import React, { useEffect, useContext, useState } from 'react';
import cn from 'classnames';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import {
  getPriorYearContribLimits,
  getCurrentYearContribLimits
} from '../../flows/shared/services/ira-contribution-limits';
import {
  DEFAULT_CURR_YR_MAX,
  DEFAULT_PRIOR_YR_MAX,
  getAppropriateLimit,
  getInputTitle,
  getExistingUserRemainingContribLimit
} from './utils';
import NotificationBanner from '../NotificationBanner/NotificationBanner';
import styles from './style.css';
import { addCommas, handleInputFormat } from '../../flows/shared/utils';
import { withDefault } from '../../core/util';
import { convertDOBToAge } from '../../flows/shared/dateUtils';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

// TODO [2021-10-19]: reduce complexity - reference issue #7
/* eslint-disable complexity */
const ContributionLimits = ({
  dob,
  priorYearState,
  setPriorYear,
  currentYearState,
  setCurrentYear,
  displayAmountError
}) => {
  const { appState, formProps } = useContext(FlowContext);
  const {
    store: {
      retailEnabled,
      priorYearAmount,
      currentYearAmount,
      loadedData = {}
    }
  } = appState;

  const { contributionPeriod = {}, contributionLimits = {} } = loadedData;

  const {
    currentYear,
    priorYear,
    withinPriorYearContributionPeriod
  } = contributionPeriod;

  const ageAtEndOfCurrentYear = convertDOBToAge(dob);

  const defaultPriorYearMaxLimitBasedOnAge = DEFAULT_PRIOR_YR_MAX(
    ageAtEndOfCurrentYear
  );
  const defaultCurrentYearMaxLimitBasedOnAge = DEFAULT_CURR_YR_MAX(
    ageAtEndOfCurrentYear
  );

  const {
    newUserLowerLimitPriorYear,
    newUserLowerLimitCurrentYear,
    existingUserPriorYearContribLimitByAge,
    existingUserCurrentYearContribLimitByAge,
    priorYearMaximumLimit,
    currentYearMaximumLimit,
    priorYearContributionsSoFar,
    priorYearRemainingAutomaticPurchases,
    currentYearContributionsSoFar,
    currentYearRemainingAutomaticPurchases,
    priorYearRemainingContributions: existingUserLimitPriorYear,
    currentYearRemainingContributions: existingUserLimitCurrentYear
  } = contributionLimits;

  // sets prior and current year limits for guest/enabled users
  // this handles any of the contribution call service failures
  // which might be a valid use case for RPS only users
  let defaultPriorYearLimit =
    !retailEnabled && newUserLowerLimitPriorYear >= 0
      ? newUserLowerLimitPriorYear
      : defaultPriorYearMaxLimitBasedOnAge;
  let defaultCurrentYearLimit =
    !retailEnabled && newUserLowerLimitCurrentYear >= 0
      ? newUserLowerLimitCurrentYear
      : defaultCurrentYearMaxLimitBasedOnAge;

  if (retailEnabled && existingUserPriorYearContribLimitByAge) {
    defaultPriorYearLimit =
      existingUserPriorYearContribLimitByAge >= 0
        ? existingUserPriorYearContribLimitByAge
        : defaultPriorYearMaxLimitBasedOnAge;
  }
  if (retailEnabled && existingUserCurrentYearContribLimitByAge) {
    defaultCurrentYearLimit =
      existingUserCurrentYearContribLimitByAge >= 0
        ? existingUserCurrentYearContribLimitByAge
        : defaultCurrentYearMaxLimitBasedOnAge;
  }

  const [priorYearLimit, setPriorYearLimit] = useState(defaultPriorYearLimit);
  const [currentYearLimit, setCurrentYearLimit] = useState(
    defaultCurrentYearLimit
  );
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [priorYearAmountValue, setPriorYearAmountValue] = useState(
    withDefault(priorYearAmount, '')
  );
  const [currYearAmountValue, setCurrYearAmountValue] = useState(
    withDefault(currentYearAmount, '')
  );

  const priorYearExistingUserRemainingLimit = getExistingUserRemainingContribLimit(
    priorYearContributionsSoFar,
    priorYearRemainingAutomaticPurchases,
    priorYearMaximumLimit,
    existingUserLimitPriorYear
  );

  const currYearExistingUserRemainingLimit = getExistingUserRemainingContribLimit(
    currentYearContributionsSoFar,
    currentYearRemainingAutomaticPurchases,
    currentYearMaximumLimit,
    existingUserLimitCurrentYear
  );

  useEffect(() => {
    const getContribLimits = async () => {
      setLoadingLimits(true);

      const priorYearLimits = await getAppropriateLimit(
        existingUserLimitPriorYear >= 0
          ? existingUserLimitPriorYear
          : defaultPriorYearMaxLimitBasedOnAge,
        getPriorYearContribLimits,
        priorYearLimit,
        retailEnabled,
        ageAtEndOfCurrentYear
      );
      const currentYearLimits = await getAppropriateLimit(
        existingUserLimitCurrentYear >= 0
          ? existingUserLimitCurrentYear
          : defaultCurrentYearMaxLimitBasedOnAge,
        getCurrentYearContribLimits,
        currentYearLimit,
        retailEnabled,
        ageAtEndOfCurrentYear
      );

      setPriorYearLimit(priorYearLimits);
      setCurrentYearLimit(currentYearLimits);

      setLoadingLimits(false);
    };
    getContribLimits();
  }, [ageAtEndOfCurrentYear]);

  const priorYearTitle = getInputTitle(
    retailEnabled,
    priorYear,
    priorYearLimit,
    priorYearMaximumLimit >= 0
      ? priorYearMaximumLimit
      : withDefault(
          existingUserPriorYearContribLimitByAge,
          defaultPriorYearMaxLimitBasedOnAge
        ),
    priorYearExistingUserRemainingLimit
  );

  const currentYearTitle = getInputTitle(
    retailEnabled,
    currentYear,
    currentYearLimit,
    currentYearMaximumLimit >= 0
      ? currentYearMaximumLimit
      : withDefault(
          existingUserCurrentYearContribLimitByAge,
          defaultCurrentYearMaxLimitBasedOnAge
        ),
    currYearExistingUserRemainingLimit,
    true
  );

  return (
    <div>
      {withinPriorYearContributionPeriod && (
        <div className={cn('contribution-amount', loadingLimits && 'blur')}>
          <NotificationBanner />

          {displayAmountError && (
            <div role="alert" aria-atomic="true" className="amount-error">
              Enter a contribution amount for current or prior year
            </div>
          )}

          <div className="first-title-line">
            Enter the amount you expect to invest. You can change this later.
          </div>
          {loadingLimits ? (
            <span>
              <InputField
                type="text"
                title={priorYearTitle}
                maxLength="18"
                onChange={e => {
                  setPriorYear(addCommas(e.target.value));
                  handleInputFormat(
                    e,
                    'DECIMAL_CURRENCY',
                    setPriorYearAmountValue
                  );
                }}
                {...formProps.priorYearAmount}
                value={withDefault(priorYearAmountValue, priorYearState)}
                disabled={loadingLimits}
                data-name={generateGeneralTestID(
                  'BasicPI',
                  'PriorYearInvestmentAmount'
                )}
              />
            </span>
          ) : (
            <InputField
              type="text"
              title={priorYearTitle}
              maxLength="18"
              onChange={e => {
                setPriorYear(addCommas(e.target.value));
                handleInputFormat(
                  e,
                  'DECIMAL_CURRENCY',
                  setPriorYearAmountValue
                );
              }}
              {...formProps.priorYearAmount}
              value={withDefault(priorYearAmountValue, priorYearState)}
              disabled={loadingLimits}
              data-name={generateGeneralTestID(
                'BasicPI',
                'PriorYearInvestmentAmount'
              )}
            />
          )}
        </div>
      )}

      <div className={cn('contribution-amount', loadingLimits && 'blur')}>
        <div className="first-title-line">
          Enter the amount you expect to invest. You can change this later.
        </div>
        {loadingLimits ? (
          <span>
            <InputField
              type="text"
              title={currentYearTitle}
              maxLength="18"
              onChange={e => {
                setCurrentYear(addCommas(e.target.value));
                handleInputFormat(
                  e,
                  'DECIMAL_CURRENCY',
                  setCurrYearAmountValue
                );
              }}
              {...formProps.currentYearAmount}
              value={withDefault(currYearAmountValue, currentYearState)}
              disabled={loadingLimits}
              data-name={generateGeneralTestID(
                'BasicPI',
                'CurrentYearInvestmentAmount'
              )}
            />
          </span>
        ) : (
          <InputField
            type="text"
            title={currentYearTitle}
            maxLength="18"
            onChange={e => {
              setCurrentYear(addCommas(e.target.value));
              handleInputFormat(e, 'DECIMAL_CURRENCY', setCurrYearAmountValue);
            }}
            {...formProps.currentYearAmount}
            value={withDefault(currYearAmountValue, currentYearState)}
            disabled={loadingLimits}
            required={!withinPriorYearContributionPeriod}
            data-name={generateGeneralTestID(
              'BasicPI',
              'CurrentYearInvestmentAmount'
            )}
          />
        )}
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable complexity */

export default ContributionLimits;
